import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  salesApi,
  Sale,
  CreateSaleDto,
  UpdateSaleDto,
  GetSalesDto,
  SalesAnalytics,
  SalesByStatusAnalytics,
} from '../api/sales';
import { safeNumber } from '../utils/formatters';

interface UseSalesReturn {
  // Data
  sales: Sale[];
  analytics: SalesAnalytics | null;
  statusAnalytics: SalesByStatusAnalytics | null;
  loading: boolean;
  error: string | null;
  
  // Operations
  createSale: (data: CreateSaleDto) => Promise<Sale | null>;
  updateSale: (id: string, data: UpdateSaleDto) => Promise<Sale | null>;
  deleteSale: (id: string) => Promise<boolean>;
  refreshData: () => Promise<void>;
  
  // Utility functions
  getSaleById: (id: string) => Sale | undefined;
  getTotalRevenue: () => number;
  getSalesByStatus: (status: string) => Sale[];
  getRecentSales: (days?: number) => Sale[];
}

export const useSales = (initialFilters: GetSalesDto = {}): UseSalesReturn => {
  const [sales, setSales] = useState<Sale[]>([]);
  const [analytics, setAnalytics] = useState<SalesAnalytics | null>(null);
  const [statusAnalytics, setStatusAnalytics] = useState<SalesByStatusAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Load sales data
  const loadSales = useCallback(async (filters: GetSalesDto = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const salesData = await salesApi.getSales({ ...initialFilters, ...filters });
      setSales(salesData);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load sales';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [initialFilters]);

  // Load analytics data
  const loadAnalytics = useCallback(async () => {
    try {
      const [analyticsData, statusData] = await Promise.all([
        salesApi.getSalesAnalytics(),
        salesApi.getSalesByStatus(),
      ]);
      
      setAnalytics(analyticsData);
      setStatusAnalytics(statusData);
    } catch (err) {
      console.error('Failed to load sales analytics:', err);
    }
  }, []);

  // Create sale
  const createSale = useCallback(async (data: CreateSaleDto): Promise<Sale | null> => {
    try {
      setLoading(true);
      const newSale = await salesApi.createSale(data);
      
      setSales(prev => [newSale, ...prev]);
      toast.success('Sale created successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return newSale;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create sale';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Update sale
  const updateSale = useCallback(async (id: string, data: UpdateSaleDto): Promise<Sale | null> => {
    try {
      setLoading(true);
      const updatedSale = await salesApi.updateSale(id, data);
      
      setSales(prev => 
        prev.map(sale => 
          sale.id === id ? updatedSale : sale
        )
      );
      
      toast.success('Sale updated successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return updatedSale;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update sale';
      setError(errorMessage);
      toast.error(errorMessage);
      return null;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Delete sale
  const deleteSale = useCallback(async (id: string): Promise<boolean> => {
    try {
      setLoading(true);
      await salesApi.deleteSale(id);
      
      setSales(prev => prev.filter(sale => sale.id !== id));
      toast.success('Sale deleted successfully');
      
      // Refresh analytics
      loadAnalytics();
      
      return true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete sale';
      setError(errorMessage);
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  }, [loadAnalytics]);

  // Refresh data
  const refreshData = useCallback(async () => {
    await Promise.all([
      loadSales(),
      loadAnalytics(),
    ]);
  }, [loadSales, loadAnalytics]);

  // Utility functions
  const getSaleById = useCallback((id: string): Sale | undefined => {
    return sales.find(sale => sale.id === id);
  }, [sales]);

  const getTotalRevenue = useCallback((): number => {
    return sales.reduce((total, sale) => total + safeNumber(sale.total), 0);
  }, [sales]);

  const getSalesByStatus = useCallback((status: string): Sale[] => {
    return sales.filter(sale => sale.status === status);
  }, [sales]);

  const getRecentSales = useCallback((days = 30): Sale[] => {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);
    
    return sales.filter(sale => new Date(sale.saleDate) >= cutoffDate);
  }, [sales]);

  // Load data on mount
  useEffect(() => {
    const loadInitialData = async () => {
      await Promise.all([
        loadSales(),
        loadAnalytics(),
      ]);
    };
    
    loadInitialData();
  }, []);

  return {
    // Data
    sales,
    analytics,
    statusAnalytics,
    loading,
    error,
    
    // Operations
    createSale,
    updateSale,
    deleteSale,
    refreshData,
    
    // Utility functions
    getSaleById,
    getTotalRevenue,
    getSalesByStatus,
    getRecentSales,
  };
}; 